import { useLocalStorage, useSource } from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { useTranslation } from "@opendash/i18n";
import { Icon } from "@opendash/icons";
import { useNavigate } from "@opendash/router";
import { Button, Descriptions, Divider, Flex, Tag } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useMemo } from "react";
import { useSourceWithChildren } from "../..";
import { Schedule_Execution } from "../../../../js/parsegenerated";
import {
  getLastExecution,
  getNextExecution,
  isScheduleOverdue,
  isScheduleRunning,
  ScheduleData,
} from "../../hooks/useScheduleData";

type Props = {
  data: ScheduleData;
};

export const ScheduleSourceDetail = observer((props: Props) => {
  const t = useTranslation();
  const nav = useNavigate();
  const [currentSource] = useSource();
  const [includeChildren] = useLocalStorage<boolean>(
    "openservice:show-children-in-schedule-table",
    false
  );
  const validIds = useSourceWithChildren(currentSource?.id);
  const validSources = useMemo(() => {
    return props.data.sources.filter((src) =>
      includeChildren ? validIds.includes(src.id) : currentSource?.id === src.id
    );
  }, [validIds, currentSource, includeChildren, props.data.sources]);

  async function handleExecute(srcId: string) {
    // get pending execution or create a new one
    let actualExecution: Schedule_Execution = undefined;
    actualExecution = await new Parse.Query(Schedule_Execution)
      .equalTo("origin", props.data.template)
      .equalTo("source", new Source({ objectId: srcId }))
      .doesNotExist("finishedAt")
      .first();

    if (!actualExecution)
      actualExecution = await new Schedule_Execution().save({
        origin: props.data.template,
        source: new Source({ id: srcId }),
        origin_cron: props.data.template.cron,
        title: props.data.template.get("title"),
      });

    nav(`/openservice/schedule/${actualExecution.id}/execute/`);
  }

  const items: DescriptionsItemType[] = useMemo(() => {
    return validSources
      .map((src) => {
        const lastDate = getLastExecution(props.data, src.id);
        const nextDate = getNextExecution(props.data, src.id);
        const lastText = lastDate
          ? dayjs(lastDate).format("DD.MM.YYYY")
          : t("maintenance:schedule.source.detail.execution.last.none");
        const nextText = nextDate
          ? dayjs(nextDate).format("DD.MM.YYYY")
          : t("maintenance:schedule.source.detail.execution.next.none");

        const isRunning = isScheduleRunning(props.data, src.id);

        return {
          label: src.get("name"),
          nextSchedule: nextDate,
          children: (
            <Flex justify="space-between" align="center">
              <Tag>
                {t("maintenance:schedule.source.detail.execution.last.label")}
                <Divider type="vertical" />
                {lastText}
              </Tag>
              <Icon icon="fa:arrow-right" />
              {!isRunning ? (
                <Tag
                  color={
                    isScheduleOverdue(props.data, src.id) ? "error" : "default"
                  }
                >
                  {t("maintenance:schedule.source.detail.execution.next.label")}
                  <Divider type="vertical" />
                  {nextText}
                </Tag>
              ) : (
                <Tag color="processing">
                  {t("maintenance:schedule.source.detail.execution.running")}
                </Tag>
              )}
              <Button type="primary" onClick={() => handleExecute(src.id)}>
                {t("maintenance:schedule.source.detail.execution.button")}
              </Button>
            </Flex>
          ),
        };
      })
      .sort((a, b) => {
        if ((dayjs(a.nextSchedule).isSame(dayjs(b.nextSchedule)), "day"))
          return a.label.localeCompare(b.label);

        return a.nextSchedule > b.nextSchedule ? 1 : -1;
      }) as DescriptionsItemType[];
  }, [validSources]);

  return <Descriptions size="small" bordered items={items} column={1} />;
});
